import { supabase } from '@/integrations/supabase/client';
import { PriceFetchService } from './PriceFetchService';
import { PositionMappingService } from './PositionMappingService';

// Note: Hard-coded mappings removed. Now using database as single source of truth via PositionMappingService

export interface TPSLCalculationParams {
  symbol: string;
  positionSize: number;  // TradingView position size
  tpPercent: number;
  slPercent: number;
  currentPrice?: number;
}

export interface TPSLCalculationResult {
  symbol: string;
  tvPositionSize: number;
  ctraderLotSize: number;
  currentPrice: number;
  positionValueUSD: number;
  tpUSD: number;
  slUSD: number;
  tpPercent: number;
  slPercent: number;
  priceSource: 'cache' | 'live' | 'manual';
  calculatedAt: Date;
}

export interface FilenameParseResult {
  symbol: string;
  tpPercent: number;
  slPercent: number;
  timeframe: string;
  isValid: boolean;
  error?: string;
}

export class TPSLCalculatorService {
  /**
   * Get custom position mappings from localStorage (kept for backward compatibility)
   */
  static getCustomPositionMappings(): Record<string, number> {
    try {
      const saved = localStorage.getItem('custom_position_mappings');
      if (saved) {
        const parsed = JSON.parse(saved);
        const mappings: Record<string, number> = {};
        Object.entries(parsed).forEach(([symbol, mapping]: [string, any]) => {
          mappings[symbol] = mapping.tvToCtraderRatio;
        });
        return mappings;
      }
    } catch (error) {
      console.error('Error loading custom position mappings:', error);
    }
    return {};
  }

  /**
   * Parse filename to extract symbol and TP/SL percentages
   * Supports patterns like:
   * - ETHUSDT - 1.8 - 5s
   * - TRXUSDT - 3.5 - 10s.xlsx
   * - TRXUSDT - 2.3/1.1 - 10s
   * - LINKUSDT.P - 2.5 - 5s (perpetual contracts)
   * - APTUSDT(T) - 1.8 - 10s (test contracts)
   * - TRXUSDT-2-10s.xlsx - List of trades.csv (no spaces around dashes)
   */
  static parseFilename(filename: string): FilenameParseResult {
    try {
      console.log('Debug: Starting to parse filename:', filename);
      
      // Step 1: Clean the filename - remove file extensions and suffixes
      let workingFilename = filename;
      
      // Remove duplicate download suffixes like (1), (2), etc.
      workingFilename = workingFilename.replace(/\s*\(\d+\)\s*/g, '');
      
      // Remove common CSV file suffixes
      workingFilename = workingFilename.replace(/\s*-\s*List of trades\.csv$/i, '');
      workingFilename = workingFilename.replace(/\.csv$/i, '');
      
      // Remove file extensions like .xlsx
      workingFilename = workingFilename.replace(/\.(xlsx|xls)$/i, '');
      
      // Trim any extra whitespace
      workingFilename = workingFilename.trim();
      
      console.log('Debug: Working filename after cleanup:', workingFilename);
      
      // Step 2: Try different patterns with flexible spacing
      
      // Pattern 1: Symbol-tp-sl-timeframe (4-part format, no spaces)
      const pattern4PartNoSpaces = /^([A-Z]+(?:USD|USDT|JPY|EUR|GBP|\.P|\(T\))?)-(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)-(\d+[smhSMH])$/i;
      const match4PartNoSpaces = workingFilename.match(pattern4PartNoSpaces);
      
      console.log('Debug: Pattern 4-part (no spaces) match result:', match4PartNoSpaces);
      
      if (match4PartNoSpaces) {
        const [, rawSymbol, tp, sl, timeframe] = match4PartNoSpaces;
        const cleanSymbol = this.cleanSymbol(rawSymbol);
        
        console.log('Debug: Extracted values (4-part no spaces):', { rawSymbol, cleanSymbol, tp, sl, timeframe });
        
        return {
          symbol: cleanSymbol,
          tpPercent: parseFloat(tp),
          slPercent: parseFloat(sl),
          timeframe: timeframe.toLowerCase(),
          isValid: true
        };
      }
      
      // Pattern 2: Symbol - tp - sl - timeframe (4-part format, with spaces)
      const pattern4PartWithSpaces = /^([A-Z]+(?:USD|USDT|JPY|EUR|GBP|\.P|\(T\))?)\s*-\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*-\s*(\d+[smhSMH])$/i;
      const match4PartWithSpaces = workingFilename.match(pattern4PartWithSpaces);
      
      console.log('Debug: Pattern 4-part (with spaces) match result:', match4PartWithSpaces);
      
      if (match4PartWithSpaces) {
        const [, rawSymbol, tp, sl, timeframe] = match4PartWithSpaces;
        const cleanSymbol = this.cleanSymbol(rawSymbol);
        
        console.log('Debug: Extracted values (4-part with spaces):', { rawSymbol, cleanSymbol, tp, sl, timeframe });
        
        return {
          symbol: cleanSymbol,
          tpPercent: parseFloat(tp),
          slPercent: parseFloat(sl),
          timeframe: timeframe.toLowerCase(),
          isValid: true
        };
      }
      
      // Pattern 3: Symbol-percentage-timeframe (same TP/SL, no spaces)
      const pattern1NoSpaces = /^([A-Z]+(?:USD|USDT|JPY|EUR|GBP|\.P|\(T\))?)-(\d+(?:\.\d+)?)-(\d+[smhSMH])$/i;
      const match1NoSpaces = workingFilename.match(pattern1NoSpaces);
      
      console.log('Debug: Pattern 1 (no spaces) match result:', match1NoSpaces);
      
      if (match1NoSpaces) {
        const [, rawSymbol, percent, timeframe] = match1NoSpaces;
        const cleanSymbol = this.cleanSymbol(rawSymbol);
        const percentage = parseFloat(percent);
        
        console.log('Debug: Extracted values (no spaces):', { rawSymbol, cleanSymbol, percent, timeframe });
        
        return {
          symbol: cleanSymbol,
          tpPercent: percentage,
          slPercent: percentage,
          timeframe: timeframe.toLowerCase(),
          isValid: true
        };
      }
      
      // Pattern 4: Symbol - percentage - timeframe (same TP/SL, with spaces)
      const pattern1WithSpaces = /^([A-Z]+(?:USD|USDT|JPY|EUR|GBP|\.P|\(T\))?)\s*-\s*(\d+(?:\.\d+)?)\s*-\s*(\d+[smhSMH])$/i;
      const match1WithSpaces = workingFilename.match(pattern1WithSpaces);
      
      console.log('Debug: Pattern 1 (with spaces) match result:', match1WithSpaces);
      
      if (match1WithSpaces) {
        const [, rawSymbol, percent, timeframe] = match1WithSpaces;
        const cleanSymbol = this.cleanSymbol(rawSymbol);
        const percentage = parseFloat(percent);
        
        console.log('Debug: Extracted values (with spaces):', { rawSymbol, cleanSymbol, percent, timeframe });
        
        return {
          symbol: cleanSymbol,
          tpPercent: percentage,
          slPercent: percentage,
          timeframe: timeframe.toLowerCase(),
          isValid: true
        };
      }
      
      // Pattern 5: Symbol-tp/sl-timeframe (different TP/SL, no spaces - legacy format)
      const pattern2NoSpaces = /^([A-Z]+(?:USD|USDT|JPY|EUR|GBP|\.P|\(T\))?)-(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)-(\d+[smhSMH])$/i;
      const match2NoSpaces = workingFilename.match(pattern2NoSpaces);
      
      console.log('Debug: Pattern 2 (no spaces) match result:', match2NoSpaces);
      
      if (match2NoSpaces) {
        const [, rawSymbol, tp, sl, timeframe] = match2NoSpaces;
        const cleanSymbol = this.cleanSymbol(rawSymbol);
        
        return {
          symbol: cleanSymbol,
          tpPercent: parseFloat(tp),
          slPercent: parseFloat(sl),
          timeframe: timeframe.toLowerCase(),
          isValid: true
        };
      }
      
      // Pattern 6: Symbol - tp/sl - timeframe (different TP/SL, with spaces - legacy format)
      const pattern2WithSpaces = /^([A-Z]+(?:USD|USDT|JPY|EUR|GBP|\.P|\(T\))?)\s*-\s*(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)\s*-\s*(\d+[smhSMH])$/i;
      const match2WithSpaces = workingFilename.match(pattern2WithSpaces);
      
      console.log('Debug: Pattern 2 (with spaces) match result:', match2WithSpaces);
      
      if (match2WithSpaces) {
        const [, rawSymbol, tp, sl, timeframe] = match2WithSpaces;
        const cleanSymbol = this.cleanSymbol(rawSymbol);
        
        return {
          symbol: cleanSymbol,
          tpPercent: parseFloat(tp),
          slPercent: parseFloat(sl),
          timeframe: timeframe.toLowerCase(),
          isValid: true
        };
      }
      
      // Pattern 7: Symbol-tp_sl-timeframe (different TP/SL, no spaces - NEW format with underscore)
      const pattern2NoSpacesUnderscore = /^([A-Z]+(?:USD|USDT|JPY|EUR|GBP|\.P|\(T\))?)-(\d+(?:\.\d+)?)_(\d+(?:\.\d+)?)-(\d+[smhSMH])$/i;
      const match2NoSpacesUnderscore = workingFilename.match(pattern2NoSpacesUnderscore);
      
      console.log('Debug: Pattern 2 underscore (no spaces) match result:', match2NoSpacesUnderscore);
      
      if (match2NoSpacesUnderscore) {
        const [, rawSymbol, tp, sl, timeframe] = match2NoSpacesUnderscore;
        const cleanSymbol = this.cleanSymbol(rawSymbol);
        
        return {
          symbol: cleanSymbol,
          tpPercent: parseFloat(tp),
          slPercent: parseFloat(sl),
          timeframe: timeframe.toLowerCase(),
          isValid: true
        };
      }
      
      // Pattern 8: Symbol - tp_sl - timeframe (different TP/SL, with spaces - NEW format with underscore)
      const pattern2WithSpacesUnderscore = /^([A-Z]+(?:USD|USDT|JPY|EUR|GBP|\.P|\(T\))?)\s*-\s*(\d+(?:\.\d+)?)_(\d+(?:\.\d+)?)\s*-\s*(\d+[smhSMH])$/i;
      const match2WithSpacesUnderscore = workingFilename.match(pattern2WithSpacesUnderscore);
      
      console.log('Debug: Pattern 2 underscore (with spaces) match result:', match2WithSpacesUnderscore);
      
      if (match2WithSpacesUnderscore) {
        const [, rawSymbol, tp, sl, timeframe] = match2WithSpacesUnderscore;
        const cleanSymbol = this.cleanSymbol(rawSymbol);
        
        return {
          symbol: cleanSymbol,
          tpPercent: parseFloat(tp),
          slPercent: parseFloat(sl),
          timeframe: timeframe.toLowerCase(),
          isValid: true
        };
      }
      
      console.log('No pattern matched for filename:', filename);
      
      return {
        symbol: '',
        tpPercent: 0,
        slPercent: 0,
        timeframe: '',
        isValid: false,
        error: `Filename format not recognized: "${filename}". Expected formats: SYMBOL-TP%-SL%-TIMEFRAME or SYMBOL-TP%-TIMEFRAME or SYMBOL - TP% - TIMEFRAME or SYMBOL-TP%_SL%-TIMEFRAME or SYMBOL-TP%/SL%-TIMEFRAME (legacy)`
      };
    } catch (error) {
      return {
        symbol: '',
        tpPercent: 0,
        slPercent: 0,
        timeframe: '',
        isValid: false,
        error: `Error parsing filename: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Clean and normalize symbol from filename
   */
  private static cleanSymbol(rawSymbol: string): string {
    return rawSymbol
      .replace(/\.(P|PERP|SPOT)$/i, '')     // Remove perpetual/spot suffixes
      .replace(/\(T\)$/i, '')               // Remove test contract suffix
      .replace(/^_+|_+$/g, '')              // Remove leading/trailing underscores
      .replace(/\s+/g, '')                  // Remove all whitespace
      .toUpperCase();
  }

  /**
   * Check database cache for price before calling API
   */
  static async checkDatabaseCache(symbol: string): Promise<{ price: number; source: 'cache' } | null> {
    try {
      const { data, error } = await supabase
        .from('csv_analysis_price_cache')
        .select('price, expires_at')
        .eq('symbol', symbol.toUpperCase())
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('Database cache check error:', error);
        return null;
      }

      if (data) {
        console.log(`📊 Database cache HIT for ${symbol}: ${data.price}`);
        return {
          price: data.price,
          source: 'cache'
        };
      }

      console.log(`📊 Database cache MISS for ${symbol}`);
      return null;
    } catch (error) {
      console.warn('Database cache check failed:', error);
      return null;
    }
  }

  /**
   * Get current price with optimized caching (DB cache → client cache → API)
   */
  static async getCurrentPrice(symbol: string): Promise<{ price: number; source: 'cache' | 'api' }> {
    try {
      // First check database cache
      const dbCacheResult = await this.checkDatabaseCache(symbol);
      if (dbCacheResult) {
        return dbCacheResult;
      }

      // If no DB cache, use existing PriceFetchService (which has client cache + API)
      return await PriceFetchService.getPrice(symbol);
    } catch (error) {
      console.warn('Error in getCurrentPrice:', error);
      throw error;
    }
  }

  /**
   * Calculate notional position value (always contractSize × lots for forex)
   */
  static calculateNotionalValue(
    symbol: string, 
    contractSpec: { contractSize: number; baseUnit: string }, 
    ctraderLotSize: number
  ): number {
    const upperSymbol = symbol.toUpperCase();
    const notionalValue = contractSpec.contractSize * ctraderLotSize;
    console.log(`💱 Notional value for ${upperSymbol}: ${contractSpec.contractSize} × ${ctraderLotSize} = ${notionalValue}`);
    return notionalValue;
  }

  /**
   * Calculate TP/SL in USD using price difference method
   */
  static async calculateTPSLInUSD(
    symbol: string,
    currentPrice: number,
    tpPercent: number,
    slPercent: number,
    contractSpec: { contractSize: number; baseUnit: string },
    ctraderLotSize: number
  ): Promise<{ tpUSD: number; slUSD: number }> {
    const upperSymbol = symbol.toUpperCase();
    
    // Calculate TP and SL prices
    const tpPrice = currentPrice * (1 + tpPercent / 100);
    const slPrice = currentPrice * (1 - slPercent / 100);
    
    console.log(`📊 Price levels for ${upperSymbol}: Entry=${currentPrice}, TP=${tpPrice}, SL=${slPrice}`);
    
    // Calculate profit/loss in base units
    const tpProfit = (tpPrice - currentPrice) * contractSpec.contractSize * ctraderLotSize;
    const slLoss = (currentPrice - slPrice) * contractSpec.contractSize * ctraderLotSize;
    
    console.log(`💰 P&L in quote currency: TP profit=${tpProfit}, SL loss=${slLoss}`);
    
    // Convert to USD based on quote currency
    const quoteCurrency = this.getQuoteCurrency(upperSymbol);
    console.log(`💱 Quote currency for ${upperSymbol}: ${quoteCurrency}`);
    
    if (quoteCurrency === 'USD' || quoteCurrency === 'USDT') {
      // Already in USD
      console.log(`✅ Already in USD: TP=${tpProfit}, SL=${slLoss}`);
      return { tpUSD: Math.abs(tpProfit), slUSD: Math.abs(slLoss) };
    }
    
    // Need to convert to USD
    try {
      const conversionRate = await PriceFetchService.getCrossRate(quoteCurrency);
      console.log(`📊 ${quoteCurrency}/USD conversion rate: ${conversionRate}`);
      
      const tpUSD = Math.abs(tpProfit * conversionRate);
      const slUSD = Math.abs(slLoss * conversionRate);
      
      console.log(`✅ Converted to USD: TP=${tpUSD}, SL=${slUSD}`);
      return { tpUSD, slUSD };
    } catch (error) {
      console.error(`❌ Failed to get conversion rate for ${quoteCurrency}:`, error);
      // Fallback: assume 1:1 ratio with warning
      console.warn(`⚠️ Using 1:1 conversion for ${quoteCurrency} - results may be inaccurate`);
      return { tpUSD: Math.abs(tpProfit), slUSD: Math.abs(slLoss) };
    }
  }

  /**
   * Extract quote currency from symbol (last 3 characters typically)
   */
  static getQuoteCurrency(symbol: string): string {
    const upperSymbol = symbol.toUpperCase();
    
    // Handle USDT case
    if (upperSymbol.endsWith('USDT')) {
      return 'USDT';
    }
    
    // Standard 6-character forex pairs (EURUSD, GBPJPY, etc.)
    if (upperSymbol.length === 6) {
      return upperSymbol.substring(3);
    }
    
    // Fallback to last 3 characters
    return upperSymbol.slice(-3);
  }

  /**
   * Convert TradingView position to cTrader lot size
   */
  static async convertTVPositionToCTraderLots(symbol: string, tvPosition: number): Promise<number> {
    console.log(`🔄 Converting TV position for ${symbol}: ${tvPosition}`);
    
    // First try database mapping
    const ratio = await PositionMappingService.getTVToCTraderRatio(symbol);
    if (ratio !== null) {
      console.log(`📊 Using database mapping for ${symbol}: ratio = ${ratio}`);
      return tvPosition * ratio;
    }
    
    // Try localStorage custom mappings as fallback
    const customMappings = this.getCustomPositionMappings();
    const upperSymbol = symbol.toUpperCase();
    
    if (customMappings[upperSymbol] !== undefined) {
      console.log(`💾 Using localStorage mapping for ${symbol}: ratio = ${customMappings[upperSymbol]}`);
      return tvPosition * customMappings[upperSymbol];
    }
    
    // Ultimate fallback with detailed error
    console.error(`❌ No position mapping found for ${symbol}. Available database mappings:`, await PositionMappingService.getAvailableSymbols());
    throw new Error(`Position mapping not found for symbol: ${symbol}. Please add a mapping in the Position Mappings admin section.`);
  }

  /**
   * Calculate TP/SL in USD from percentage
   */
  static async calculateTPSLFromPercentage(params: TPSLCalculationParams): Promise<TPSLCalculationResult> {
    const { symbol, positionSize, tpPercent, slPercent } = params;
    
    // Get current price
    let currentPrice: number;
    let priceSource: 'cache' | 'live' | 'manual';
    
    if (params.currentPrice) {
      currentPrice = params.currentPrice;
      priceSource = 'manual';
    } else {
      const priceResult = await this.getCurrentPrice(symbol);
      currentPrice = priceResult.price;
      priceSource = priceResult.source === 'cache' ? 'cache' : 'live';
    }

    // Convert TV position to cTrader lots
    const ctraderLotSize = await this.convertTVPositionToCTraderLots(symbol, positionSize);
    
    // Get contract specifications from database
    const contractSpec = await PositionMappingService.getContractSpecs(symbol);
    if (!contractSpec) {
      throw new Error(`Contract specifications not found for symbol: ${symbol}. Please add the symbol to Position Mappings.`);
    }
    
    console.log(`📋 Using contract specs for ${symbol}:`, contractSpec);

    // Calculate notional position value (for display purposes)
    const positionValueUSD = this.calculateNotionalValue(symbol, contractSpec, ctraderLotSize);

    // Calculate TP/SL in USD using price difference method
    const { tpUSD, slUSD } = await this.calculateTPSLInUSD(
      symbol,
      currentPrice,
      tpPercent,
      slPercent,
      contractSpec,
      ctraderLotSize
    );

    console.debug('TPSL calculation debug', {
      symbol: symbol.toUpperCase(),
      tvPositionSize: positionSize,
      ctraderLotSize,
      currentPrice,
      contractSize: contractSpec.contractSize,
      positionValueUSD,
      tpPercent,
      slPercent,
      tpUSD,
      slUSD,
      priceSource,
    });

    return {
      symbol: symbol.toUpperCase(),
      tvPositionSize: positionSize,
      ctraderLotSize,
      currentPrice,
      positionValueUSD,
      tpUSD,
      slUSD,
      tpPercent,
      slPercent,
      priceSource,
      calculatedAt: new Date()
    };
  }

  /**
   * Extract position sizes from CSV file content
   */
  static async extractPositionSizesFromCsv(filename: string): Promise<{ 
    minPositionSize: number; 
    allPositionSizes: number[]; 
    hasPositionData: boolean 
  }> {
    try {
      // Resolve storage path for this filename from DB (latest match)
      const { data: uploadRow, error: uploadErr } = await supabase
        .from('user_csv_uploads')
        .select('storage_path, created_at')
        .eq('filename', filename)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (uploadErr) {
        console.warn('Could not query user_csv_uploads for', filename, uploadErr);
      }

      if (!uploadRow?.storage_path) {
        console.warn('No storage_path found for filename', filename);
        return { minPositionSize: 0.01, allPositionSizes: [], hasPositionData: false };
      }

      // Download the file from the proper bucket using storage_path
      const { data: fileBlob, error: dlErr } = await supabase.storage
        .from('user-csv')
        .download(uploadRow.storage_path);

      if (dlErr || !fileBlob) {
        console.warn('Download failed for', uploadRow.storage_path, dlErr);
        return { minPositionSize: 0.01, allPositionSizes: [], hasPositionData: false };
      }

      const csvText = await fileBlob.text();

      // Parse CSV using papaparse
      const Papa = (await import('papaparse')).default;
      const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true, transformHeader: (h: string) => h.trim() });

      if (parsed.errors.length > 0) {
        console.warn('CSV parsing errors:', parsed.errors);
      }

      // Look for position size columns (case-insensitive)
      const candidateCols = [
        'position size (qty)',
        'position size qty',
        'position size',
        'qty',
        'position_size',
        'quantity',
        'size',
        'lot_size'
      ];

      const fields = (parsed.meta.fields || []).map((f: string) => f.trim());
      const lowerToActual = new Map<string, string>();
      fields.forEach((f: string) => lowerToActual.set(f.toLowerCase(), f));

      let positionColumnName = '';
      for (const cand of candidateCols) {
        const actual = lowerToActual.get(cand.toLowerCase());
        if (actual) { positionColumnName = actual; break; }
      }

      if (!positionColumnName) {
        console.warn('No position size column found in CSV for', filename, 'fields:', fields);
        return { minPositionSize: 0.01, allPositionSizes: [], hasPositionData: false };
      }

      // Extract and sanitize numeric position sizes
      const toNumber = (v: any): number => {
        if (v === undefined || v === null) return NaN;
        const s = v.toString().replace(/[,\s]/g, '');
        const n = parseFloat(s);
        return isNaN(n) ? NaN : Math.abs(n);
      };

      const positionSizes: number[] = [];
      (parsed.data as any[]).forEach((row: any) => {
        const raw = row[positionColumnName];
        const n = toNumber(raw);
        if (!isNaN(n) && n > 0) positionSizes.push(n);
      });

      if (positionSizes.length === 0) {
        console.warn('No valid position sizes parsed for', filename);
        return { minPositionSize: 0.01, allPositionSizes: [], hasPositionData: false };
      }

      const minPositionSize = Math.min(...positionSizes);
      console.debug('Extracted position sizes for', filename, { min: minPositionSize, count: positionSizes.length });

      return {
        minPositionSize: minPositionSize > 0 ? minPositionSize : 0.01,
        allPositionSizes: positionSizes,
        hasPositionData: true
      };
    } catch (error) {
      console.error('Error extracting position sizes from CSV:', error);
      return { minPositionSize: 0.01, allPositionSizes: [], hasPositionData: false };
    }
  }

  /**
   * Bulk check database cache for multiple symbols
   */
  static async bulkCheckDatabaseCache(symbols: string[]): Promise<Map<string, { price: number; source: 'cache' }>> {
    const cacheResults = new Map<string, { price: number; source: 'cache' }>();
    
    if (symbols.length === 0) return cacheResults;

    try {
      const upperSymbols = symbols.map(s => s.toUpperCase());
      
      const { data, error } = await supabase
        .from('csv_analysis_price_cache')
        .select('symbol, price, expires_at')
        .in('symbol', upperSymbols)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Bulk database cache check error:', error);
        return cacheResults;
      }

      if (data) {
        // Use latest entry for each symbol
        const symbolPrices = new Map<string, number>();
        data.forEach(row => {
          if (!symbolPrices.has(row.symbol)) {
            symbolPrices.set(row.symbol, row.price);
          }
        });

        symbolPrices.forEach((price, symbol) => {
          cacheResults.set(symbol, { price, source: 'cache' });
        });

        console.log(`📊 Bulk DB cache: ${cacheResults.size}/${symbols.length} symbols found in cache`);
      }
    } catch (error) {
      console.warn('Bulk database cache check failed:', error);
    }

    return cacheResults;
  }

  /**
   * Batch calculate TP/SL for multiple files using CSV data
   */
  static async batchCalculateFromFilenames(filenames: string[]): Promise<Map<string, TPSLCalculationResult | Error>> {
    const results = new Map<string, TPSLCalculationResult | Error>();
    
    // Parse all filenames first
    const parseResults = filenames.map(filename => ({
      filename,
      parsed: this.parseFilename(filename)
    }));

    // Extract unique symbols for bulk cache check
    const validSymbols = parseResults
      .filter(({ parsed }) => parsed.isValid)
      .map(({ parsed }) => parsed.symbol);
    
    const uniqueSymbols = [...new Set(validSymbols)];
    
    // Bulk check database cache for all symbols
    const dbCache = await this.bulkCheckDatabaseCache(uniqueSymbols);
    console.log(`📊 Bulk price cache check: ${dbCache.size}/${uniqueSymbols.length} symbols cached`);

    // Calculate for each valid file
    for (const { filename, parsed } of parseResults) {
      if (!parsed.isValid) {
        results.set(filename, new Error(parsed.error || 'Invalid filename format'));
        continue;
      }

      try {
        // Extract position size from CSV file
        const positionData = await this.extractPositionSizesFromCsv(filename);
        const positionSize = positionData.hasPositionData ? positionData.minPositionSize : 0.01;
        
        console.log(`Using position size ${positionSize} for ${filename} (from CSV: ${positionData.hasPositionData})`);
        
        // Use cached price if available
        const cachedPrice = dbCache.get(parsed.symbol.toUpperCase());
        
        const calculation = await this.calculateTPSLFromPercentage({
          symbol: parsed.symbol,
          positionSize,
          tpPercent: parsed.tpPercent,
          slPercent: parsed.slPercent,
          currentPrice: cachedPrice?.price // Use cached price if available
        });

        results.set(filename, calculation);
      } catch (error) {
        results.set(filename, error instanceof Error ? error : new Error('Unknown calculation error'));
      }
    }

    return results;
  }

  /**
   * Get available symbols for position mapping
   */
  static async getAvailableSymbols(): Promise<string[]> {
    // Combine position mappings with price fetch supported symbols
    const positionSymbols = await PositionMappingService.getAvailableSymbols();
    const priceSymbols = await PriceFetchService.getSupportedSymbols();
    return [...new Set([...positionSymbols, ...priceSymbols])];
  }

  /**
   * Check if symbol is supported
   */
  static async isSymbolSupported(symbol: string): Promise<boolean> {
    try {
      // Check if symbol exists in database mappings
      const ratio = await PositionMappingService.getTVToCTraderRatio(symbol);
      const contractSpec = await PositionMappingService.getContractSpecs(symbol);
      const priceSupported = await PriceFetchService.isSymbolSupported(symbol);
      
      return ratio !== null && contractSpec !== null && priceSupported;
    } catch {
      return false;
    }
  }
}